<template>
	<div id="cl-vr-chart-modal-widget">
		<h3 class="module-title">
			Volatility & Returns
			<slot name="superscript3" />
		</h3>
		<ReadMoreLink :useAlternateContainer="true" />
		<div>
			<p class="chart-description">
				For Class {{ shareClass }} Shares, this chart tracks the 10-year standard deviation
				<slot name="superscript4" />
				<slot name="standardDeviation" />
				and 10-year annualized {{ isCIT ? 'net' : '' }} return of {{ assetClass === 'fixedIncome' ? 'fixed income' : 'equity' }} {{ fundLabel }}.
			</p>
		</div>
		<div v-if="hasDualCharts">
			<a
				class="sub-title"
				:class="{active: selectedAssetClass === 'equity'}"
				@click="setSelectedAssetClass('equity')"
			>Equity Funds</a>
			<a
				class="sub-title divider-line"
				:class="{active: selectedAssetClass ==='fixedIncome'}"
				@click="setSelectedAssetClass('fixedIncome')"
			>Fixed Income Funds</a>
		</div>
		<div v-else-if="assetClass === 'fixedIncome'">
			<div class="sub-title">Fixed Income</div>
		</div>
		<div v-else>
			<div class="sub-title">Equities</div>
		</div>
		<div class="chart-legend">
			<span class="colored-square gray" />
			<span class="index">Index</span>
			<div class="dropdown">
				<select>
					<option>{{ indexName }}</option>
				</select>
			</div>
			<slot name="superscript5" />
			<slot name="volatilityIndexId" />
		</div>
		<div id="volatility-large-chart-container" v-resize="handleChartLoad">
			<img ref="mainChart" :src="activeChart" />
		</div>
		<div
			class="as-of-dates"
		>Standard Deviation as of {{ deviationAsOf }}. Annualized return as of {{ returnsAsOf }}</div>
		<div
			class="chart-disclaimer"
		>Volatility & Return chart is not available for {{ fundLabel }} less than 10 years old.</div>
	</div>
</template>

<script>
import ReadMoreLink from '_components_/Shared/ReadMoreLink';
import * as tooltipsjs from '_assets_/js/tooltips.js';
import resize from 'vue-resize-directive';

export default {
	name: 'VolatilityLargeChart',
	directives: {
		resize
	},
	components: {
		ReadMoreLink
	},
	props: [
		'volatilityChart',
		'volatilityEquityChart',
		'volatilityFixedIncomeChart',
		'volatilityEquityChartData',
		'volatilityFixedIncomeChartData',
		'audience',
		'volatilityChartData',
		'widgetData'
	],
	data() {
		return {
			selectedAssetClass: 'equity'
		};
	},
	computed: {
		isCIT() {
			return this.componentData.isCIT;
		},
		componentData() {
			return this.widgetData || {};
		},
		activeChart() {
			let chart = '';
			if (this.volatilityChart || this.componentData.volatilityChart) {
				chart =
					this.volatilityChart || this.componentData.volatilityChart;
			} else if (this.selectedAssetClass === 'equity') {
				chart =
					this.volatilityEquityChart ||
					this.componentData.volatilityEquityChart;
			} else {
				chart =
					this.volatilityFixedIncomeChart ||
					this.componentData.volatilityFixedIncomeChart ||
					'';
			}
			return chart;
		},
		assetClass() {
			if (this.volatilityChartData.assetClass === 'Fixed Income') {
				return 'fixedIncome';
			}
			return 'equity';
		},
		shareClass() {
			return this.volatilityChartData.shareClass || '';
		},
		indexName() {
			let indexName = '';
			let data = this.widgetData || this.volatilityChartData;
			if (this.hasDualCharts) {
				if (this.selectedAssetClass === 'fixedIncome') {
					data = this.volatilityFixedIncomeChartData;
				} else {
					data = this.volatilityEquityChartData;
				}
			}
			if (data.IndexData) {
				indexName = data.IndexData.FundName;
			}
			return indexName;
		},
		//The V&R chart uses the plural version of the fund label
		//It's a separate property because it's too hard to convert from strategy to strategies, etc
		//the default is funds but you can pass it something else such as strategies
		fundLabel() {
			return this.componentData.fundLabelPlural || 'funds';
		},
		deviationAsOf() {
			const data = this.widgetData || this.volatilityChartData;
			return data.deviationAsOf || '--';
		},
		returnsAsOf() {
			const data = this.widgetData || this.volatilityChartData;
			return data.returnsAsOf || '--';
		},
		componentAudience() {
			return this.audience || this.componentData.audience || '';
		},
		hasDualCharts() {
			return (
				!!this.volatilityEquityChart ||
				(this.widgetData && this.widgetData.volatilityEquityChart)
			);
		},
		chartData() {
			let data =
				(Object.keys(this.componentData).length &&
					this.componentData) ||
				this.volatilityChartData;
			if (this.hasDualCharts) {
				if (this.selectedAssetClass === 'fixedIncome') {
					data = this.volatilityFixedIncomeChartData;
				} else {
					data = this.volatilityEquityChartData;
				}
			}
			return data;
		},
		strategyData() {
			return this.chartData.strategyData || this.chartData.FundData || {};
		},
		indexData() {
			return this.componentData.IndexData || this.chartData.IndexData;
		}
	},
	watch: {
		activeChart: {
			immediate: true,
			handler() {
				this.$nextTick(() => {
					this.handleChartLoad();
				});
			}
		}
	},
	methods: {
		handleChartLoad() {
			this.setChartScale();
			this.attachChartEvents();
		},
		setSelectedAssetClass(assetClass) {
			this.selectedAssetClass = assetClass;
			this.attachChartEvents();
		},
		async removeChartAnchors(parent) {
			const chartAnchors = [
				...parent.getElementsByClassName('chart-point'),
				...parent.getElementsByClassName('toolTip')
			];
			for (const item of chartAnchors) {
				await item.parentNode.removeChild(item);
			}
		},
		async attachChartEvents() {
			const strategyData = this.strategyData || {};
			const indexData = this.indexData || {};
			const chartContainer = document.getElementById(
				'volatility-large-chart-container'
			);
			await this.removeChartAnchors(chartContainer);
			for (let i = 0; i < strategyData.length; i++) {
				const dataPoint = strategyData[i];
				const chartAnchor = this.createChartAnchor(
					dataPoint,
					chartContainer
				);
				if (chartAnchor) {
					chartContainer.appendChild(chartAnchor);

					const xScale = dataPoint.X * this.scale;
					const yScale = dataPoint.Y * this.scale;
					const toolTip = tooltipsjs.createTooltip(
						xScale,
						yScale,
						this.createToolTipBody(dataPoint)
					);

					toolTip.classList.add('toolTip');
					chartContainer.appendChild(toolTip);
					chartAnchor.addEventListener(
						'mouseenter',
						this.showToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'mouseleave',
						this.hideToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'touchstart',
						this.toggleToolTip.bind(null, toolTip),
						false
					);
				}
			}
			if (indexData.X && indexData.Y) {
				const chartAnchor = this.createChartAnchor(
					indexData,
					chartContainer
				);
				if (chartAnchor) {
					chartContainer.appendChild(chartAnchor);
					const indexDataX = indexData.X * this.scale;
					const indexDataY = indexData.Y * this.scale;
					const toolTip = tooltipsjs.createTooltip(
						indexDataX,
						indexDataY,
						this.createToolTipBody(indexData)
					);

					toolTip.classList.add('toolTip');
					chartContainer.appendChild(toolTip);
					chartAnchor.addEventListener(
						'mouseenter',
						this.showToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'mouseleave',
						this.hideToolTip.bind(null, toolTip),
						false
					);
					chartAnchor.addEventListener(
						'touchstart',
						this.toggleToolTip.bind(null, toolTip),
						false
					);
				}
			}
			this.chartEventsAttached = true;
		},
		createChartAnchor(dataPoint, chartContainer) {
			const chartContainerDimenions = chartContainer.getBoundingClientRect();
			const chartMaxHeight = chartContainerDimenions.height;
			const chartMaxWidth = chartContainerDimenions.width;
			const chartAnchor = document.createElement('a');
			const xScale = (dataPoint.X - 5) * this.scale;
			const yScale = dataPoint.Y * this.scale - 4;
			const top = `top:${yScale}px`;
			const left = `left:${xScale}px`;
			const widthAndHeight = `height: ${10 * this.scale}px; width: ${10 *
				this.scale}px;`;

			chartAnchor.classList.add('chart-point');
			chartAnchor.setAttribute(
				'style',
				`${top}; ${left}; position: absolute; ${widthAndHeight}`
			);
			/*eslint-disable*/
			// debugger;
			return yScale > chartMaxHeight || xScale > chartMaxWidth
				? null
				: chartAnchor;
		},
		createToolTipBody(dataPoint) {
			const body = document.createElement('div');
			const innerContainer = document.createElement('div');
			innerContainer.style.marginTop = '3px';
			innerContainer.style.borderTop = `3px solid ${this.getColor(
				dataPoint.Objective
			)}`;
			innerContainer.style.padding = '5px';

			const title = document.createElement('div');
			const titleText = document.createTextNode(
				dataPoint.Name || dataPoint.FundName || 'Undefined Name'
			);
			title.appendChild(titleText);
			title.style.fontFamily = 'Avenir Next LT W01 Demi';
			title.style.whiteSpace = 'nowrap';

			innerContainer.appendChild(title);
			innerContainer.appendChild(this.getAnnualReturn(dataPoint));
			innerContainer.appendChild(this.getStdDeviation(dataPoint));
			body.appendChild(innerContainer);

			return body;
		},
		// TODO: merge getAnnualReturn and getStdDeviation into one function
		getAnnualReturn(dataPoint) {
			const annualReturn =
				dataPoint.AnnualReturn ||
				dataPoint.AnnualizedReturn10Yr ||
				'--';
			const annualData = document.createElement('span');
			const annualDataText = document.createTextNode(annualReturn);
			annualData.appendChild(annualDataText);
			annualData.style.fontFamily = 'Avenir Next LT W01 Demi';
			annualData.style.paddingRight = '3px';

			const annualLabel = document.createElement('span');
			const annualText = document.createTextNode(
				'10-Yr Annualized Return'
			);
			annualLabel.appendChild(annualText);
			annualLabel.style.color = '#666';
			annualLabel.style.fontSize = '12px';

			const annual = document.createElement('div');
			annual.appendChild(annualData);
			annual.appendChild(annualLabel);
			annual.style.borderBottom = '1px solid #ccc';
			annual.style.marginBottom = '5px';
			annual.style.paddingBottom = '5px';

			return annual;
		},
		getStdDeviation(dataPoint) {
			const standardDeviation =
				dataPoint.StD || dataPoint.StandardDeviation10Yr || '--';
			const stdData = document.createElement('span');
			const stdDataText = document.createTextNode(standardDeviation);
			stdData.appendChild(stdDataText);
			stdData.style.fontFamily = 'Avenir Next LT W01 Demi';
			stdData.style.paddingRight = '3px';

			const stdLabel = document.createElement('span');
			const stdText = document.createTextNode('10-Yr Standard Deviation');
			stdLabel.appendChild(stdText);
			stdLabel.style.color = '#666';
			stdLabel.style.fontSize = '12px';

			const std = document.createElement('div');
			std.appendChild(stdData);
			std.appendChild(stdLabel);

			return std;
		},
		showToolTip(toolTip) {
			this.hideAllToolTips();
			toolTip.style.display = 'block';
			const leftModify = toolTip.offsetWidth / 2;
			const arrowModify = leftModify - 20;
			const topModify = toolTip.offsetHeight + 30;
			const leftValue = toolTip.dataset.left - leftModify;

			const children = toolTip.childNodes;
			children[1].style.left = `${arrowModify}px`;
			children[2].style.left = `${arrowModify + 1}px`;

			toolTip.style.top = `${toolTip.dataset.top - topModify}px`;
			toolTip.style.left = `${leftValue}px`;
			toolTip.style.whiteSpace = 'nowrap';

			const chartContainer = document.getElementById(
				'volatility-large-chart-container'
			);
			const containerWidth = chartContainer.offsetWidth;
			const rightFlush = containerWidth - toolTip.offsetWidth;

			if (leftValue < -13) {
				const adjustArrow = -13 - leftValue;
				const arrowLeft = parseInt(children[1].style.left, 10);
				toolTip.style.left = '-13px';
				children[1].style.left = `${arrowLeft - adjustArrow}px`;
				children[2].style.left = `${arrowLeft - adjustArrow + 1}px`;
			}
			if (leftValue > rightFlush) {
				const adjustArrow = rightFlush - leftValue;
				const arrowLeft = parseInt(children[1].style.left, 10);
				toolTip.style.left = `${rightFlush}px`;
				children[1].style.left = `${arrowLeft - adjustArrow}px`;
				children[2].style.left = `${arrowLeft - adjustArrow + 1}px`;
			}
		},
		hideToolTip(toolTip) {
			toolTip.style.display = 'none';
		},
		toggleToolTip(toolTip) {
			if (toolTip.style.display === 'none') {
				this.showToolTip(toolTip);
			} else {
				this.hideToolTip(toolTip);
			}
		},
		hideAllToolTips() {
			const tips = document.getElementsByClassName('toolTip');
			for (let i = 0; i < tips.length; i++) {
				tips[i].style.display = 'none';
			}
		},
		setChartScale() {
			//From the chart inputs in the Node API
			const originalWidth = 1150;
			const chartContainer = document.getElementById(
				'volatility-large-chart-container'
			);
			const containerWidth = chartContainer.offsetWidth;
			const scale = containerWidth / originalWidth;
			if (scale <= 0) {
				this.scale = 1;
			} else {
				this.scale = scale;
			}
		},
		getColor(objective) {
			const colors = {
				Growth: '#1E5AA0',
				'Growth &amp Income': '#009ADF',
				'Equity-Income': '#37B8A9',
				'Money Market Funds': '#7C8A8D',
				Bond: '#773C7F',
				Balanced: '#37B8A9',
				'Tax-Exempt Bond': '#773C7F',
				'Portfolio Series': '#00736C',
				'Retirement Target Date': '#00736C',
				'College Target Date': '#00736C',
				index: '#333',
				default: '#009adf'
			};

			if (objective && Object.prototype.hasOwnProperty.call(colors, objective)) {
				return colors[objective];
			}
			return colors['default'];
		}
	}
};
</script>

<style scoped lang="less">
@import '~_css_/common.less';
#volatility-large-chart-container {
	position: relative;
}
#cl-vr-chart-modal-widget {
	.read-disclosures-link {
		margin-bottom: 20px;
		a {
			cursor: pointer;
			font-family: Avenir Next LT W01 Demi, Arial, sans-serif;
			font-weight: 700;
		}
	}
	.sub-title {
		font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
		font-size: 14px;
		font-weight: 500;
		&.active {
			color: @dark-gray;
		}
	}
	a.sub-title {
		color: #009adf;
	}
	.chart-description {
		font-weight: 400;
		font-size: 14px;
		color: #333;
		font-family: 'Avenir Next LT W01 Demi', Arial, serif;
		margin-bottom: 18px;
	}
	.chart-legend {
		margin-bottom: 10px;
		.colored-square {
			display: inline-block;
			height: 10px;
			margin-right: 5px;
			width: 10px;
			&.gray {
				background-color: gray;
			}
		}
		.index {
			color: rgb(102, 102, 102);
			font-family: 'AvenirNextLTW01-Regular', Arial, sans-serif;
		}
		.dropdown {
			margin-right: 5px;
		}
	}
	img {
		width: 100%;
	}
	.as-of-dates {
		margin-top: 13px;
		font-size: 12px;
		font-family: 'AvenirNextLTW01-Condens 721299', Arial, sans-serif;
		color: #666;
	}
	.chart-disclaimer {
		margin-top: 20px;
		font-size: 14px;
		color: #333;
		font-family: 'AvenirNextLTW01-Regular', Arial, sans-serif;
		line-height: 18px;
	}
}
</style>
